import { useState } from "react";

type CheckResult = "covered" | "call" | "invalid" | null;

const COVERED_PREFIXES = ["DA", "ME"];
const CALL_PREFIXES = ["TN", "BR", "CT", "SE", "CR", "KT"];

function getResult(input: string): CheckResult {
  const clean = input.trim().toUpperCase().replace(/\s/g, "");
  if (clean.length < 2) return "invalid";
  const match = clean.match(/^([A-Z]{1,2})\d/);
  if (!match) return "invalid";
  const prefix = match[1];
  if (COVERED_PREFIXES.includes(prefix)) return "covered";
  if (CALL_PREFIXES.includes(prefix)) return "call";
  return "call";
}

const coverageAreas = [
  "Northfleet", "Gravesend", "Dartford", "Swanscombe",
  "Bluewater", "Ebbsfleet", "Greenhithe", "Longfield",
  "Gravesham", "Medway", "Chatham", "Rochester",
];

export default function PostcodeChecker() {
  const [postcode, setPostcode] = useState("");
  const [result, setResult] = useState<CheckResult>(null);

  const handleCheck = () => {
    if (!postcode.trim()) return;
    setResult(getResult(postcode));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleCheck();
  };

  return (
    <section className="py-16 bg-white border-t border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#EBF4FF] rounded-2xl mb-5">
            <i className="ri-map-pin-2-fill text-[#1E88E5] text-2xl"></i>
          </div>
          <h2
            className="font-heading text-[#0F2040] leading-tight mb-3"
            style={{ fontSize: "clamp(1.5rem, 2.5vw, 2rem)", fontWeight: 800 }}
          >
            Do We Cover Your Area?
          </h2>
          <p className="text-gray-500 font-body text-base mb-8">
            Enter your postcode below to instantly check if we service your location across Northfleet, Gravesend and Kent.
          </p>

          {/* Input Row */}
          <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto mb-6">
            <input
              type="text"
              value={postcode}
              onChange={(e) => { setPostcode(e.target.value); setResult(null); }}
              onKeyDown={handleKeyDown}
              placeholder="e.g. DA11 7AB"
              maxLength={8}
              className="flex-1 bg-[#F8FAFE] border border-gray-200 rounded-lg px-4 py-3.5 text-sm font-body text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#1E88E5] focus:ring-2 focus:ring-[#1E88E5]/20 transition-all uppercase tracking-widest"
            />
            <button
              onClick={handleCheck}
              className="flex items-center justify-center gap-2 bg-[#1E88E5] hover:bg-[#1565C0] text-white font-heading font-semibold text-sm px-7 py-3.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line"></i>
              Check Coverage
            </button>
          </div>

          {/* Result */}
          {result === "covered" && (
            <div className="flex items-center justify-center gap-3 bg-green-50 border border-green-200 rounded-xl px-6 py-4 max-w-md mx-auto mb-6 animate-pulse-once">
              <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-full flex-shrink-0">
                <i className="ri-check-line text-green-600 text-lg"></i>
              </div>
              <div className="text-left">
                <p className="font-heading font-bold text-green-800 text-sm">Great news — we cover your area!</p>
                <p className="text-green-700 text-xs font-body mt-0.5">Scroll down to request your free quote.</p>
              </div>
            </div>
          )}

          {result === "call" && (
            <div className="flex items-center justify-center gap-3 bg-amber-50 border border-amber-200 rounded-xl px-6 py-4 max-w-md mx-auto mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-amber-100 rounded-full flex-shrink-0">
                <i className="ri-phone-line text-amber-600 text-lg"></i>
              </div>
              <div className="text-left">
                <p className="font-heading font-bold text-amber-800 text-sm">Give us a call to confirm</p>
                <p className="text-amber-700 text-xs font-body mt-0.5">
                  We may cover your area — call us on{" "}
                  <a href="tel:02084322244" className="underline font-semibold cursor-pointer">0208 43 222 44</a>.
                </p>
              </div>
            </div>
          )}

          {result === "invalid" && (
            <div className="flex items-center justify-center gap-3 bg-red-50 border border-red-200 rounded-xl px-6 py-4 max-w-md mx-auto mb-6">
              <div className="w-9 h-9 flex items-center justify-center bg-red-100 rounded-full flex-shrink-0">
                <i className="ri-error-warning-line text-red-500 text-lg"></i>
              </div>
              <p className="text-red-700 text-sm font-body">Please enter a valid UK postcode (e.g. DA11 7AB).</p>
            </div>
          )}

          {/* Coverage tag cloud */}
          <div className="mt-6">
            <p className="text-gray-400 text-xs font-body mb-3 uppercase tracking-wider">Areas we regularly cover</p>
            <div className="flex flex-wrap justify-center gap-2">
              {coverageAreas.map((area) => (
                <span
                  key={area}
                  className="inline-flex items-center gap-1 bg-[#F0F6FF] text-[#1A3A6B] text-xs font-body font-medium px-3 py-1.5 rounded-full border border-[#D6E8FF]"
                >
                  <i className="ri-map-pin-2-fill text-[#1E88E5] text-xs"></i>
                  {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
